// MARK://dhk8/admin-shell/pages/modules/ImpersonatePage
// Manager override / "Ver como…" — solo accesible para `owner` (no manager).
// Lista usuarios del tenant (owner / manager / cajero / mesero / cocina) y
// permite suplantar a uno para hacer soporte / overrides sin pedirle su PIN.
//
// Flow:
//   1. POST /api/users/:id/impersonate            (dhk8-platform-auth) — emite token
//   2. POST /api/audit/log IMPERSONATION_START    (dhk8-platform-audit)
//   3. authStore.impersonateUser(...)             → AdminLayout muestra banner
//   4. Al salir (banner "Salir" en AdminLayout): authStore.stopImpersonating()
//      + POST /api/audit/log IMPERSONATION_END    (registrado aquí en useEffect)
//
// Hoy el handler POST /api/users/:id/impersonate no existe; el cliente degrada
// a un no-op y la suplantación se queda como UI-only flag (igual de visible).
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShieldAlert, UserCheck } from 'lucide-react'
import { listUsers, STAFF_ROLE_LABEL, type StaffUser } from '../../lib/clients/users.js'
import { startImpersonation, logImpersonation } from '../../lib/clients/impersonate.js'
import { useAuthStore } from '../../stores/auth-store.js'
import { useAsync, asyncState } from '../../lib/use-async.js'

export default function ImpersonatePage() {
  const session = useAuthStore((s) => s.session)
  const impersonateUser = useAuthStore((s) => s.impersonateUser)
  const list = useAsync((s) => listUsers(undefined, s))
  const nav = useNavigate()
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function viewAs(u: StaffUser) {
    if (!session) return
    if (u.id === session.userId) return
    setBusy(u.id); setError(null)
    try {
      const tok = await startImpersonation(u.id)
      await logImpersonation({
        event: 'IMPERSONATION_START',
        tenantId: session.tenantId,
        actorUserId: session.userId,
        targetUserId: u.id,
        targetName: u.fullName,
      })
      impersonateUser({ userId: u.id, fullName: u.fullName, role: u.role, token: tok?.sessionToken ?? null })
      nav('/dashboard')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo iniciar la suplantación')
    } finally {
      setBusy(null)
    }
  }

  // Banner "Suplantando: …" + botón "Salir" viven en AdminLayout. Si el usuario
  // ya está suplantando cuando entra aquí, le ofrecemos un atajo.
  const isImpersonating = !!session?.impersonating

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">Ver como… (Manager override)</h1>
          <p className="adm-page__sub">Suplanta a un miembro del personal para soporte / overrides. Cada acción queda registrada en el log de auditoría.</p>
        </div>
      </div>

      {error && <div className="adm-banner adm-banner--danger" role="alert">{error}</div>}

      {isImpersonating && (
        <div className="adm-card" role="status">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldAlert size={16} aria-hidden style={{ color: 'var(--pos-warning)' }} />
            <strong>Actualmente estás suplantando a {session?.impersonating?.name}.</strong>
            <span style={{ color: 'var(--pos-fg-tertiary)' }}>Sal desde el banner superior antes de cambiar de usuario.</span>
          </div>
        </div>
      )}

      {asyncState(list.loading, list.error, list.reload)}
      {list.data && (
        <div className="adm-card adm-card--pad0">
          <div className="adm-list">
            {list.data.length === 0 && <div className="adm-empty">Sin usuarios en este tenant</div>}
            {list.data.map((u) => {
              const self = session?.userId === u.id
              return (
                <div key={u.id} className="adm-row">
                  <span style={{ color: 'var(--pos-fg-tertiary)' }}><UserCheck size={18} /></span>
                  <div className="adm-row__main">
                    <div className="adm-row__title">{u.fullName} {self && <span className="adm-badge adm-badge--info">tú</span>}</div>
                    <div className="adm-row__meta">{STAFF_ROLE_LABEL[u.role as keyof typeof STAFF_ROLE_LABEL] ?? u.role} · {u.email ?? '—'} · PIN {u.pinId ?? '—'}</div>
                  </div>
                  <div className="adm-row__tail">
                    <span className={`adm-badge ${u.status === 'ACTIVE' ? 'adm-badge--ok' : 'adm-badge--warn'}`}>{u.status === 'ACTIVE' ? 'Activo' : 'Suspendido'}</span>
                    <button
                      className="adm-btn adm-btn--sm adm-btn--primary"
                      onClick={() => viewAs(u)}
                      disabled={self || isImpersonating || busy === u.id || u.status !== 'ACTIVE'}
                      title={self ? 'No puedes suplantarte a ti mismo' : 'Ver el panel como este usuario'}
                    >
                      {busy === u.id ? 'Conectando…' : 'Ver como…'}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

/** Hook the AdminLayout's `Salir` banner button uses to flush the audit trail
 *  when the user ends a session. Registered globally in main.tsx is not feasible
 *  (no access to session at module load), so we expose this helper and the
 *  AdminLayout calls it. */
export function useFlushImpersonationEnd() {
  const session = useAuthStore((s) => s.session)
  useEffect(() => {
    // No-op: the actual flush happens in AdminLayout when the user clicks
    // "Salir". This hook is reserved for future cross-tab coordination.
    void session
  }, [session])
}
